import { DeleteOutlined, MoreVert } from '@mui/icons-material';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useDeleteItemMutation } from 'api/ordering-service/ordering-service';
import { TransformItemResult } from 'api/ordering-service/ordering-service.types';
import AuthorizationGate from 'components/authorization-gate/authorization-gate.component';
import ConfirmationDrawer from 'components/drawers/confirmation-drawer/confirmation-drawer.component';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

type ItemsPopoverProps = {
  itemData: TransformItemResult;
};

const ItemsPopover = ({ itemData }: ItemsPopoverProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const deleteConfirmationDrawer = useUtilityDrawer();
  const [deleteItem] = useDeleteItemMutation();

  const itemPopupState = usePopupState({
    variant: 'popover',
    popupId: 'item-popup',
  });

  const onConfirmationValid = async () => {
    try {
      await deleteItem({ itemId: itemData?.itemId }).unwrap();

      enqueueSnackbar(
        t('ItemsDataGrid.actions.delete.alertMessage', {
          itemTitle: itemData?.title,
        }),
        { severity: 'success' },
      );
      deleteConfirmationDrawer.close();
    } catch (_e) {}
  };

  return (
    <>
      <AuthorizationGate permission="DELETE_ITEM">
        <IconButton
          {...bindTrigger(itemPopupState)}
          data-testid="ItemInfo.IconButton"
          aria-label={t('forms.itemsForm.itemCard.actions.aria-label')}
        >
          <MoreVert />
        </IconButton>
      </AuthorizationGate>
      <Menu {...bindMenu(itemPopupState)}>
        <AuthorizationGate permission="DELETE_ITEM">
          <MenuItem
            onClick={() => {
              itemPopupState.close();
              deleteConfirmationDrawer.setOpen(true);
            }}
          >
            <ListItemIcon>
              <DeleteOutlined color="error" />
            </ListItemIcon>
            <ListItemText
              primary={t('forms.itemsForm.itemCard.actions.delete.label')}
            />
          </MenuItem>
        </AuthorizationGate>
      </Menu>
      <ConfirmationDrawer
        open={deleteConfirmationDrawer.open}
        close={deleteConfirmationDrawer.close}
        subtitle={t('drawer.itemDeleteDrawer.subtitle', {
          itemTitle: itemData?.title,
        })}
        submitButtonLabel={t('drawer.itemDeleteDrawer.submitButtonLabel')}
        onValid={onConfirmationValid}
        cancel={deleteConfirmationDrawer.close}
        color="error"
      />
    </>
  );
};

export default ItemsPopover;
